import type { Task } from '../api';

/**
 * How far a task's subtasks have got: `K/N`.
 *
 * Shared rather than redrawn per surface, which is the whole point — the parity spec exists
 * because the last three defects the owner reported were each one surface drifting from the rest.
 *
 * Three rules that are easy to get wrong, all of them here rather than at four call sites:
 *
 * - **Nothing at zero.** A task with no subtasks says nothing; it never reads `0/0`.
 * - **The number carries the meaning.** The done tone reinforces `N/N`, it does not encode it, so
 *   the pill still says the same thing to someone who cannot see the green (SC 1.4.1).
 * - **Dropped work is already out of `N`** — the service leaves it out of the denominator, so a
 *   parent whose last unfinished subtask is dropped reaches `N/N` rather than stranding at 4/5.
 */
export function SubtaskPill({ task }: { task: Pick<Task, 'subtasks'> }) {
  const { done, total } = task.subtasks;
  if (total === 0) return null;
  const complete = done === total;
  return (
    <span
      className={complete ? 'pill ok subtask-count' : 'pill subtask-count'}
      data-testid="subtask-count"
      /*
        "subtasks", said rather than left to the number. A task showing `0/3` for its checklist in
        the full view sat in a list beside tasks showing `0/2` and `0/3` for their subtasks, so the
        badge that did appear was ambiguous about what it counted. `ChecklistLine`'s own title is
        the matching half.
      */
      title={`${done} of ${total} subtasks done`}
    >
      {done}/{total}
    </span>
  );
}
